import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import PaginationBar from "@/components/common/PaginationBar";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { useMediaQuery } from "../../../lib/hooks";
import { IconFileInvoice, IconRefresh, IconTrash } from "@tabler/icons-react";
import { notifications } from "../../../lib/notifications";
import paymentService from "../../../services/paymentService";
import { useAuthStore } from "../../../store/authStore";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../components/ui/tooltip";

const BRAND = { r: 255, g: 153, b: 0 };
const BRAND_HEADER = { r: 194, g: 110, b: 0 };
const BRAND_DARK = { r: 90, g: 40, b: 0 };

const STATUS_COLORS = {
    SUCCESS:   { r: 47,  g: 158, b: 68  },
    PENDING:   { r: 245, g: 159, b: 0   },
    FAILED:    { r: 224, g: 49,  b: 49  },
    CANCELLED: { r: 134, g: 142, b: 150 },
};

const STATUS_LABELS = {
    SUCCESS: 'PAID', PENDING: 'PENDING', FAILED: 'FAILED', CANCELLED: 'CANCELLED',
};

async function loadLogoDataUrl() {
    return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth || 512;
            canvas.height = img.naturalHeight || 125;
            canvas.getContext('2d').drawImage(img, 0, 0);
            resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = () => resolve(null);
        img.src = '/images/invoice-logo.png';
    });
}

async function generateInvoice(tx, user) {
    const { jsPDF } = await import('jspdf');
    const logoDataUrl = await loadLogoDataUrl();
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const W = doc.internal.pageSize.getWidth();
    const sc = STATUS_COLORS[tx.status] || STATUS_COLORS.CANCELLED;
    const statusLabel = STATUS_LABELS[tx.status] || tx.status;

    const date = new Date(tx.createdAt).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'long', year: 'numeric',
    });
    const time = new Date(tx.createdAt).toLocaleTimeString('en-US', {
        hour: '2-digit', minute: '2-digit', hour12: true,
    });

    doc.setFillColor(BRAND_DARK.r, BRAND_DARK.g, BRAND_DARK.b);
    doc.rect(0, 0, W, 72, 'F');

    if (logoDataUrl) {
        doc.addImage(logoDataUrl, 'PNG', 32, 17, 170, 37);
    } else {
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(22);
        doc.setFont('helvetica', 'bold');
        doc.text('SohozKaj', 40, 46);
    }

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(17);
    doc.setFont('helvetica', 'bold');
    doc.text('INVOICE', W - 40, 48, { align: 'right' });

    doc.setFillColor(sc.r, sc.g, sc.b);
    doc.roundedRect(W - 125, 82, 90, 22, 4, 4, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(statusLabel, W - 80, 97, { align: 'center' });

    doc.setTextColor(60, 60, 60);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Invoice No:  ${tx.orderId}`, 40, 98);
    doc.text(`Date:            ${date},  ${time}`, 40, 112);
    doc.text(`Status:            ${statusLabel}`, 40, 126);

    doc.setDrawColor(230, 230, 230);
    doc.line(40, 138, W - 40, 138);

    const colMid = W / 2;

    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(BRAND.r, BRAND.g, BRAND.b);
    doc.text('FROM', 40, 158);

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 30);
    doc.setFontSize(11);
    doc.text('Sigmative LLC', 40, 172);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(80, 80, 80);
    doc.text('sohozkaj.com', 40, 186);
    doc.text('hello@sigmative.com', 40, 200);
    doc.text('+880 1700 55 95 95', 40, 214);
    doc.text('Bogura - 5800, Bangladesh', 40, 228);

    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(BRAND.r, BRAND.g, BRAND.b);
    doc.text('BILL TO', colMid + 20, 158);

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 30);
    doc.setFontSize(11);
    doc.text(user?.name || 'Customer', colMid + 20, 172);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(80, 80, 80);
    let billY = 186;
    if (user?.email)   { doc.text(user.email,   colMid + 20, billY); billY += 14; }
    if (user?.phone)   { doc.text(user.phone,   colMid + 20, billY); billY += 14; }
    if (user?.address) { doc.text(user.address, colMid + 20, billY); billY += 14; }

    doc.setDrawColor(230, 230, 230);
    doc.line(40, 248, W - 40, 248);

    const tableY = 260;

    doc.setFillColor(BRAND.r, BRAND.g, BRAND.b);
    doc.rect(40, tableY, W - 80, 24, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Description', 52, tableY + 16);
    doc.text('Credits', W - 220, tableY + 16);
    doc.text('Amount', W - 52, tableY + 16, { align: 'right' });

    doc.setFillColor(252, 248, 240);
    doc.rect(40, tableY + 24, W - 80, 30, 'F');
    doc.setTextColor(30, 30, 30);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Credit Recharge — ${tx.credits.toLocaleString()} Credits`, 52, tableY + 44);
    doc.text(tx.credits.toLocaleString(), W - 220, tableY + 44);
    doc.text(`${tx.amount} ${tx.gateway === 'FASTSPRING' ? 'USD' : 'BDT'}`, W - 52, tableY + 44, { align: 'right' });

    doc.setDrawColor(230, 230, 230);
    doc.rect(40, tableY, W - 80, 54, 'S');
    doc.line(40, tableY + 24, W - 40, tableY + 24);

    // FastSpring (Merchant of Record) adds tax on top of our package price — captured in
    // metadata. Show a Tax row + tax-inclusive Total for those; BD gateways stay 2-row.
    const curLabel = tx.gateway === 'FASTSPRING' ? 'USD' : 'BDT';
    const taxAmt = (tx.gateway === 'FASTSPRING' && tx.metadata?.tax != null) ? Number(tx.metadata.tax) : null;
    const hasTax = taxAmt != null && !Number.isNaN(taxAmt);
    const totalAmt = hasTax
        ? (tx.metadata?.total != null ? Number(tx.metadata.total) : tx.amount + taxAmt)
        : tx.amount;

    const totalY = tableY + 74;
    const boxH = hasTax ? 74 : 54;
    doc.setFillColor(255, 247, 230);
    doc.roundedRect(W - 220, totalY, 180, boxH, 4, 4, 'F');
    doc.setDrawColor(BRAND.r, BRAND.g, BRAND.b);
    doc.roundedRect(W - 220, totalY, 180, boxH, 4, 4, 'S');

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text('Sub-total', W - 210, totalY + 16);
    doc.text(`${tx.amount} ${curLabel}`, W - 52, totalY + 16, { align: 'right' });

    let lineY = totalY + 24;
    let totalTextY = totalY + 40;
    if (hasTax) {
        doc.text('Tax', W - 210, totalY + 32);
        doc.text(`${taxAmt.toFixed(2)} ${curLabel}`, W - 52, totalY + 32, { align: 'right' });
        lineY = totalY + 44;
        totalTextY = totalY + 60;
    }

    doc.setDrawColor(220, 200, 160);
    doc.line(W - 210, lineY, W - 52, lineY);

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(BRAND_DARK.r, BRAND_DARK.g, BRAND_DARK.b);
    doc.text('Total', W - 210, totalTextY);
    doc.text(`${hasTax ? totalAmt.toFixed(2) : tx.amount} ${curLabel}`, W - 52, totalTextY, { align: 'right' });

    if (tx.paymentMethod) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text(`Payment Method: ${tx.paymentMethod}`, 40, totalY + 42);
    }

    const footerY = doc.internal.pageSize.getHeight() - 52;

    doc.setFillColor(BRAND.r, BRAND.g, BRAND.b);
    doc.rect(0, footerY - 4, W, 2, 'F');

    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(BRAND_DARK.r, BRAND_DARK.g, BRAND_DARK.b);
    doc.text('Thank you for using SohozKaj!', W / 2, footerY + 14, { align: 'center' });

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(150, 150, 150);
    doc.text('For support: hello@sigmative.com  |  +880 1700 55 95 95', W / 2, footerY + 28, { align: 'center' });

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(150, 150, 150);
    doc.text('Sigmative LLC, Bogura - 5800, Bangladesh', W / 2, footerY + 42, { align: 'center' });

    doc.save(`invoice-${tx.orderId}.pdf`);
}

const PAGE_SIZE = 20;

/* Badge color map for status */
const BADGE_STYLES = {
    green:  "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
    red:    "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
    gray:   "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300",
    orange: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
};

function StatusBadge({ color = 'gray', label }) {
    return (
        <span className={`inline-flex items-center shrink-0 rounded-[7px] px-[11px] py-[3px] text-[12px] font-semibold ${BADGE_STYLES[color] || BADGE_STYLES.gray}`}>
            {label}
        </span>
    );
}

export default function PaymentHistoryTable() {
    const { t, i18n } = useTranslation();
    const fmtNum = (n) => n.toLocaleString(i18n.language === 'bn' ? 'bn-BD' : 'en-US');
    const { user, getCurrentUser } = useAuthStore();
    const isMobile = useMediaQuery('(max-width: 768px)');
    const [allTransactions, setAllTransactions] = useState(null);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [recheckingIds, setRecheckingIds] = useState(new Set());
    const [deleteTarget, setDeleteTarget] = useState(null); // the payment row the user is about to delete
    const [deleting, setDeleting] = useState(false);

    const STATUS_BADGE = {
        SUCCESS:   { color: "green",  label: t('payments.statusSuccess')   },
        FAILED:    { color: "red",    label: t('payments.statusFailed')     },
        PENDING:   { color: "orange", label: t('payments.statusPending')    },
        CANCELLED: { color: "orange", label: t('payments.statusCancelled')  },
    };

    useEffect(() => {
        const fetchAll = async () => {
            try {
                setHistoryLoading(true);
                // Fetch a large batch so we can filter client-side without pagination issues
                const data = await paymentService.getHistory({ page: 1, limit: 200 });
                setAllTransactions(data?.transactions || []);
                setPage(1);
            } catch (err) {
                notifications.show({
                    title: t('payments.notifErrorTitle'),
                    message: err.message || t('payments.notifHistoryFailed'),
                    color: "red",
                });
            } finally {
                setHistoryLoading(false);
            }
        };
        fetchAll();
    }, []);

    // Verify a payment via our backend, which re-queries the gateway that processed it.
    // Route by the row's gateway: SSLCommerz rows go to the SSLCommerz verify endpoint
    // (queried by our tran_id/orderId), Moneybag rows to the Moneybag verify endpoint.
    const handleRecheck = async (tx) => {
        setRecheckingIds(prev => new Set(prev).add(tx.orderId));
        try {
            const result = tx.gateway === 'SSLCOMMERZ'
                ? await paymentService.verifyPaymentSSL(tx.orderId)
                : await paymentService.verifyPayment(tx.gatewayTxnId || tx.orderId);
            if (result.status === 'SUCCESS') {
                await getCurrentUser();
                notifications.show({
                    title: t('payments.notifSuccessTitle'),
                    message: t('payments.notifRecheckSuccess', { credits: result.credits }),
                    color: 'green',
                });
            } else {
                notifications.show({ message: t('payments.notifRecheckFailed'), color: 'red' });
            }
            const updated = await paymentService.getHistory({ page: 1, limit: 200 });
            setAllTransactions(updated?.transactions || []);
            setPage(1);
        } catch {
            notifications.show({ message: t('payments.notifRecheckFailed'), color: 'red' });
        } finally {
            setRecheckingIds(prev => { const s = new Set(prev); s.delete(tx.orderId); return s; });
        }
    };

    // Soft delete: the row leaves the user's history (the backend keeps it for the admin Trash).
    const handleDelete = async () => {
        if (!deleteTarget || deleting) return;
        setDeleting(true);
        try {
            await paymentService.deletePayment(deleteTarget.orderId);
            const remaining = (allTransactions || []).filter((x) => x.orderId !== deleteTarget.orderId);
            setAllTransactions(remaining);
            setPage((p) => Math.min(p, Math.max(1, Math.ceil(remaining.length / PAGE_SIZE))));
            notifications.show({ message: t('payments.notifDeleted'), color: 'green' });
            setDeleteTarget(null);
        } catch (err) {
            notifications.show({ message: err.message || t('payments.notifDeleteFailed'), color: 'red' });
        } finally {
            setDeleting(false);
        }
    };

    // Delete button for one row — a PENDING payment can still be settled by the gateway, so it has none.
    // `hoverOnly` (desktop table): the button fades in when the row is hovered/focused; its slot is always reserved so the
    // other action buttons never shift.
    const deleteButton = (tx, size, hoverOnly = false) => tx.status === 'PENDING' ? (hoverOnly ? <span aria-hidden="true" className="h-7 w-7 shrink-0" /> : null) : (
        <Tooltip>
            <TooltipTrigger asChild>
                <button
                    type="button"
                    aria-label={t('payments.deleteBtn')}
                    className={`inline-flex items-center justify-center h-7 w-7 rounded-md text-red-500 border border-red-200 dark:border-red-900 hover:bg-red-50 dark:hover:bg-red-950/30 shrink-0 transition-all ${hoverOnly ? 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100' : ''}`}
                    onClick={() => setDeleteTarget(tx)}
                >
                    <IconTrash size={size} />
                </button>
            </TooltipTrigger>
            <TooltipContent>{t('payments.deleteBtn')}</TooltipContent>
        </Tooltip>
    );

    // Client-side pagination on filtered results
    const totalPages = allTransactions ? Math.ceil(allTransactions.length / PAGE_SIZE) : 0;
    const pageTransactions = allTransactions
        ? allTransactions.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
        : [];

    return (
        <div className="font-hind bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-xl shadow-sm overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-[22px] py-4 border-b border-zinc-200 dark:border-zinc-700">
                <h4 className="text-base font-bold text-zinc-800 dark:text-zinc-100 m-0">{t('payments.historyTitle')}</h4>
            </div>

            {historyLoading ? (
                <div className="flex flex-col gap-3 p-[22px]">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="h-10 rounded-lg bg-zinc-100 dark:bg-zinc-800 animate-pulse" />
                    ))}
                </div>
            ) : !allTransactions?.length ? (
                <span className="block text-center py-8 text-sm text-zinc-400 dark:text-zinc-500">
                    {t('payments.noHistory')}
                </span>
            ) : (
                <>
                    {isMobile ? (
                        /* ── Mobile: card list ── */
                        <div className="flex flex-col gap-2 p-3">
                            {pageTransactions.map((tx) => {
                                const meta = STATUS_BADGE[tx.status] || { color: "gray", label: tx.status };
                                return (
                                    <div key={tx.id} className="border border-zinc-200 dark:border-zinc-700 rounded-lg px-3 py-2.5 bg-white dark:bg-zinc-900">
                                        {/* Row 1: order id + action btns */}
                                        <div className="flex items-center justify-between flex-nowrap mb-1">
                                            <span className="flex-1 min-w-0 truncate text-xs font-mono text-zinc-500 dark:text-zinc-400">
                                                {tx.orderId}
                                            </span>
                                            <TooltipProvider delayDuration={300}>
                                                <div className="flex items-center gap-1 shrink-0">
                                                    {tx.status === 'SUCCESS' && (
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <button
                                                                    className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-sky-600 border border-sky-200 dark:border-sky-900 hover:bg-sky-50 dark:hover:bg-sky-950/30 text-xs font-semibold shrink-0 transition-colors"
                                                                    onClick={() => generateInvoice(tx, user).catch(console.error)}
                                                                >
                                                                    <IconFileInvoice size={14} />
                                                                    {t('payments.invoiceBtn') || 'Invoice'}
                                                                </button>
                                                            </TooltipTrigger>
                                                            <TooltipContent>{t('payments.downloadInvoice')}</TooltipContent>
                                                        </Tooltip>
                                                    )}
                                                    {tx.status === 'PENDING' && tx.gateway !== 'FASTSPRING' && (
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <button
                                                                    disabled={recheckingIds.has(tx.orderId)}
                                                                    className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-amber-600 border border-amber-200 dark:border-amber-900 hover:bg-amber-50 dark:hover:bg-amber-950/30 text-xs font-semibold shrink-0 disabled:opacity-50 disabled:cursor-default transition-colors"
                                                                    onClick={() => handleRecheck(tx)}
                                                                >
                                                                    <IconRefresh size={14} className={recheckingIds.has(tx.orderId) ? "animate-spin" : ""} />
                                                                    {t('payments.recheckBtn')}
                                                                </button>
                                                            </TooltipTrigger>
                                                            <TooltipContent>{t('payments.recheckBtn')}</TooltipContent>
                                                        </Tooltip>
                                                    )}
                                                    {deleteButton(tx, 14)}
                                                </div>
                                            </TooltipProvider>
                                        </div>
                                        <hr className="border-0 border-t border-zinc-200 dark:border-zinc-700 mt-1 mb-1.5" />
                                        {/* Row 2: amount + credits + status */}
                                        <div className="flex items-center justify-between flex-nowrap">
                                            <div className="flex items-center gap-1.5 flex-nowrap">
                                                <span className="font-extrabold text-sm text-zinc-800 dark:text-zinc-100">{tx.gateway === 'FASTSPRING' ? `$${fmtNum(tx.amount)}` : `${fmtNum(tx.amount)}৳`}</span>
                                                <span className="text-xs text-zinc-400">·</span>
                                                <span className={`text-sm font-bold ${tx.status === "SUCCESS" ? "text-emerald-600" : "text-zinc-400 dark:text-zinc-500"}`}>
                                                    {tx.status === "SUCCESS" ? `+${fmtNum(tx.credits)}` : fmtNum(tx.credits)}
                                                </span>
                                                <span className="text-xs text-zinc-400">{t('payments.creditsLabel') || 'ক্রেডিট'}</span>
                                            </div>
                                            <StatusBadge color={meta.color} label={meta.label} />
                                        </div>
                                        {/* Row 3: method + date */}
                                        <div className="flex items-center gap-1.5 mt-1 flex-nowrap">
                                            {tx.paymentMethod && (
                                                <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400">{tx.paymentMethod}</span>
                                            )}
                                            <span className="text-xs text-zinc-400 dark:text-zinc-500">
                                                {new Date(tx.createdAt).toLocaleString("bn-BD")}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        /* ── Desktop: full table ── */
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[680px] border-collapse text-sm">
                                <thead>
                                    <tr className="text-left">
                                        {[t('payments.colOrderId'), t('payments.colAmount'), t('payments.colCredits'), t('payments.colStatus'), t('payments.colMethod'), t('payments.colDate'), t('payments.colActions')].map((h, i) => (
                                            <th key={i} className="py-3 px-4 first:pl-[22px] last:pr-[22px] last:text-right text-[12px] font-semibold text-zinc-500 dark:text-zinc-400 border-b border-zinc-200 dark:border-zinc-700">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {pageTransactions.map((tx) => {
                                        const meta = STATUS_BADGE[tx.status] || { color: "gray", label: tx.status };
                                        return (
                                            <tr key={tx.id} className="group border-t border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors">
                                                <td className="py-[13px] px-4 first:pl-[22px]">
                                                    <span className="text-[12px] font-mono text-zinc-700 dark:text-zinc-300">{tx.orderId}</span>
                                                </td>
                                                <td className="py-[13px] px-4 text-[13.5px] font-bold text-zinc-800 dark:text-zinc-100">{tx.gateway === 'FASTSPRING' ? `$${fmtNum(tx.amount)}` : `${fmtNum(tx.amount)}৳`}</td>
                                                <td className="py-[13px] px-4 text-[13.5px] font-bold">
                                                    <span className={tx.status === "SUCCESS" ? "text-emerald-600" : "text-zinc-400 dark:text-zinc-500"}>
                                                        {tx.status === "SUCCESS" ? `+${fmtNum(tx.credits)}` : fmtNum(tx.credits)}
                                                    </span>
                                                </td>
                                                <td className="py-[13px] px-4">
                                                    <StatusBadge color={meta.color} label={meta.label} />
                                                </td>
                                                <td className="py-[13px] px-4 text-[13.5px] text-zinc-700 dark:text-zinc-300">{tx.paymentMethod || "-"}</td>
                                                <td className="py-[13px] px-4 whitespace-nowrap text-[13px] text-zinc-500 dark:text-zinc-400">{new Date(tx.createdAt).toLocaleString("bn-BD")}</td>
                                                <td className="py-[13px] px-4 last:pr-[22px]">
                                                    <TooltipProvider delayDuration={300}>
                                                        <div className="flex items-center justify-end gap-1">
                                                            {tx.status === 'SUCCESS' && (
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <button
                                                                            className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-sky-600 border border-sky-200 dark:border-sky-900 hover:bg-sky-50 dark:hover:bg-sky-950/30 text-xs font-semibold transition-colors"
                                                                            onClick={() => generateInvoice(tx, user).catch(console.error)}
                                                                        >
                                                                            <IconFileInvoice size={15} />
                                                                            {t('payments.invoiceBtn') || 'Invoice'}
                                                                        </button>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>{t('payments.downloadInvoice')}</TooltipContent>
                                                                </Tooltip>
                                                            )}
                                                            {tx.status === 'PENDING' && tx.gateway !== 'FASTSPRING' && (
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <button
                                                                            disabled={recheckingIds.has(tx.orderId)}
                                                                            className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-amber-600 border border-amber-200 dark:border-amber-900 hover:bg-amber-50 dark:hover:bg-amber-950/30 text-xs font-semibold disabled:opacity-50 disabled:cursor-default transition-colors"
                                                                            onClick={() => handleRecheck(tx)}
                                                                        >
                                                                            <IconRefresh size={15} className={recheckingIds.has(tx.orderId) ? "animate-spin" : ""} />
                                                                            {t('payments.recheckBtn')}
                                                                        </button>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>{t('payments.recheckBtn')}</TooltipContent>
                                                                </Tooltip>
                                                            )}
                                                            {deleteButton(tx, 15, true)}
                                                        </div>
                                                    </TooltipProvider>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {totalPages > 1 && (
                        <div className="px-[22px] py-4 border-t border-zinc-200 dark:border-zinc-700">
                            <PaginationBar
                                page={page}
                                totalPages={totalPages}
                                total={allTransactions.length}
                                pageSize={PAGE_SIZE}
                                currentCount={pageTransactions.length}
                                onPage={setPage}
                            />
                        </div>
                    )}
                </>
            )}

            <ConfirmDialog
                open={!!deleteTarget}
                onClose={() => { if (!deleting) setDeleteTarget(null); }}
                onConfirm={handleDelete}
                loading={deleting}
                title={t('payments.deleteConfirmTitle')}
                description={deleteTarget ? t('payments.deleteConfirmDesc', { orderId: deleteTarget.orderId }) : ''}
                confirmLabel={t('payments.deleteBtn')}
            />
        </div>
    );
}
